// We're accessing local storage in a third party.
// - Take care to prefix data to prevent collisions
// - Serialize properly

const scope = "zapier";

function getKey(key: string): string {
  return [scope, key].join(".");
}

/**
 * Get a value from localStorage under a zapier-prefixed key
 * @param key the key (not prefixed)
 */
function localStorageGet(key: string): any {
  return JSON.parse(localStorage.getItem(getKey(key)) ?? "null");
}

/**
 * Set a value from localStorage under a zapier-prefixed key
 * @param key the key (not prefixed)
 * @param value the value to store (not JSON.stringified)
 */
export function localStorageSet(key: string, value: unknown): void {
  localStorage.setItem(getKey(key), JSON.stringify(value));
}

/**
 * Remove a value from localStorage under a zapier-prefixed key
 * @param key the key (not prefixed)
 */
export function localStorageRemove(key: string): void {
  localStorage.removeItem(getKey(key));
}

/**
 * Get a value from localStorage. If it doesn't exist, return the fallback value
 * **without storing it**.
 * @param key string to access
 * @param fallback called with no arguments to generate a value
 * @returns the value that exists in localStorage under the zapier-prefixed key
 */
export function localStorageGetWithFallback(
  key: string,
  fallback: () => any,
): any {
  const result = localStorageGet(key);
  if (result) {
    return result;
  } else {
    const value = fallback();
    return value;
  }
}

/**
 * Get a value from localStorage. If it doesn't exist, create it, **store it**,
 * and return it.
 * @param key string to access
 * @param fallback called with no arguments to generate a value
 * @returns the value that exists in localStorage under the zapier-prefixed key
 */
export function localStorageGetOrStoreFallback(
  key: string,
  fallback: () => any,
): any {
  return localStorageGetWithFallback(key, () => {
    const value = fallback();
    localStorageSet(key, value);
    return value;
  });
}
